import type { TFunction } from 'i18next';
import type { Promotion, PromotionAppliesTo } from '../../types/restaurant';
import type { Category, MenuItem } from '../../types/menu';

export interface PromotionMatch {
  rule: Promotion;
  discountPercent: number;
  discountAmountCents: number;
}

function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Pick the single most-favorable active promotion for the cart.
 * Mirrors resource_api catalog/promotions.py select_promotion — keep in sync.
 * `subtotalCents` is an integer in cents. `orderMode` is 'emporter' or 'livraison'.
 * Returns null when none match.
 *
 * DF-0212 — deux bases, comme côté serveur :
 * - `subtotalCents` porte le **seuil** (`minSubtotal`) : c'est le panier entier,
 *   pour qu'exclure les boissons ne change pas la règle commerciale ;
 * - `eligibleSubtotalCents` porte le **pourcentage** : la part du panier dont
 *   le produit ET sa catégorie acceptent la remise (`eligibleDiscountSubtotal`).
 *   Omis ⇒ le panier entier, soit le comportement d'avant DF-0212.
 * Si le client et le devis serveur divergent ici, la commande part en
 * `total_mismatch` : les deux calculs doivent rester identiques.
 */
export function selectPromotion(
  promotions: Promotion[],
  subtotalCents: number,
  orderMode: 'emporter' | 'livraison',
  eligibleSubtotalCents: number = subtotalCents,
  today: string = todayIso(),
): PromotionMatch | null {
  let best: PromotionMatch | null = null;

  promotions.forEach((rule) => {
    if (!rule.active) return;
    const applies: PromotionAppliesTo = rule.appliesTo ?? 'both';
    if (applies !== 'both' && applies !== orderMode) return;
    if (rule.validFrom && today < rule.validFrom) return;
    if (rule.validTo && today > rule.validTo) return;
    const percent = Number(rule.discountPercent);
    if (!Number.isFinite(percent) || percent < 1 || percent > 100) return;
    const minCents = Math.round(Number(rule.minSubtotal) * 100);
    if (subtotalCents < minCents) return;
    // Half-up cent rounding mirrors resource_api Decimal quantize:
    const discountAmountCents = Math.round((eligibleSubtotalCents * percent) / 100);
    if (best === null || discountAmountCents > best.discountAmountCents) {
      best = { rule, discountPercent: percent, discountAmountCents };
    }
  });

  // Une règle qui s'applique mais ne remise rien (tout le panier est exclu) ne
  // doit pas être annoncée : le serveur renvoie 0 %, un bandeau « −10 % » ici
  // ferait diverger le total et la commande partirait en `total_mismatch`.
  if (best !== null && (best as PromotionMatch).discountAmountCents <= 0) return null;

  return best;
}

/**
 * DF-0212 — la part du panier qu'une promotion a le droit de remiser, en
 * centimes. Une ligne est éligible si son produit ET sa catégorie portent
 * `allowOrderDiscount` — même conjonction que resource_api, et le décochage de
 * la seule catégorie suffit (la cascade vers les produits est optionnelle).
 * Une ligne dont le plat n'est plus au menu reste éligible : c'est déjà le
 * comportement d'avant, et le devis serveur reste l'autorité.
 */
export function eligibleDiscountSubtotal(
  lines: { itemId: string; unitPrice: number; qty: number }[],
  menu:
    | { items?: MenuItem[]; categories?: Category[] }
    | null
    | undefined,
): number {
  const wholeCart = () => lines.reduce((acc, l) => acc + l.unitPrice * l.qty, 0);
  // Le menu peut n'être pas encore chargé, ou n'être qu'à moitié là : sans
  // catalogue on ne sait rien exclure, et le devis serveur reste l'autorité.
  const items = menu?.items;
  const categories = menu?.categories;
  if (!items || !categories) return wholeCart();
  const itemById = new Map(items.map((i) => [i.id, i]));
  const categoryById = new Map(categories.map((c) => [c.id, c]));
  return lines.reduce((acc, line) => {
    const item = itemById.get(line.itemId);
    const category = item ? categoryById.get(item.categoryId) : undefined;
    const allowed =
      (item?.allowOrderDiscount ?? true) && (category?.allowOrderDiscount ?? true);
    return allowed ? acc + line.unitPrice * line.qty : acc;
  }, 0);
}

/**
 * The nearest rule the cart does NOT yet qualify for, for an upsell hint.
 * Returns the unmet rule with the smallest gap (in cents), or null.
 */
export function nextPromotionHint(
  promotions: Promotion[],
  subtotalCents: number,
  orderMode: 'emporter' | 'livraison',
  today: string = todayIso(),
): { rule: Promotion; gapCents: number } | null {
  let nearest: { rule: Promotion; gapCents: number } | null = null;

  promotions.forEach((rule) => {
    if (!rule.active) return;
    const applies: PromotionAppliesTo = rule.appliesTo ?? 'both';
    if (applies !== 'both' && applies !== orderMode) return;
    if (rule.validFrom && today < rule.validFrom) return;
    if (rule.validTo && today > rule.validTo) return;
    const minCents = Math.round(Number(rule.minSubtotal) * 100);
    if (subtotalCents >= minCents) return; // already qualifies
    const gapCents = minCents - subtotalCents;
    if (nearest === null || gapCents < nearest.gapCents) {
      nearest = { rule, gapCents };
    }
  });

  return nearest;
}

/**
 * Active promotions valid today (date window + percent sanity), not filtered
 * by order mode — used to advertise offers before the customer picks a mode.
 */
export function activePromotions(
  promotions: Promotion[],
  today: string = todayIso(),
): Promotion[] {
  return promotions.filter((rule) => {
    if (!rule.active) return false;
    if (rule.validFrom && today < rule.validFrom) return false;
    if (rule.validTo && today > rule.validTo) return false;
    const percent = Number(rule.discountPercent);
    return Number.isFinite(percent) && percent >= 1 && percent <= 100;
  });
}

/**
 * One marquee line per active promotion, e.g. "🎁 −10 % dès 15 € en livraison".
 * Derived at render time so the banner always reflects the current rules.
 * DF-0428 — with `t`, the generated words follow the customer's language
 * (`marquee.promo_*`); a label typed by the restaurant stays as typed.
 */
export function promotionMarqueeMessages(
  promotions: Promotion[],
  today: string = todayIso(),
  t?: TFunction,
): string[] {
  return activePromotions(promotions, today).map((rule) => {
    if (t) {
      const base =
        (rule.label ?? '').trim() ||
        t('marquee.promo_default', { percent: rule.discountPercent, min: rule.minSubtotal });
      const key =
        rule.appliesTo === 'emporter'
          ? 'marquee.promo_takeaway'
          : rule.appliesTo === 'livraison'
            ? 'marquee.promo_delivery'
            : 'marquee.promo_both';
      return `🎁 ${t(key, { base })}`;
    }
    const base =
      (rule.label ?? '').trim() ||
      `−${rule.discountPercent} % dès ${rule.minSubtotal} €`;
    const mode =
      rule.appliesTo === 'emporter'
        ? ' à emporter'
        : rule.appliesTo === 'livraison'
          ? ' en livraison'
          : '';
    return `🎁 ${base}${mode}`;
  });
}
